import React, { FC, useContext } from 'react';
import clsx from 'clsx';
import {
  AppBar,
  Typography,
  Toolbar,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Fade,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@material-ui/icons/Menu';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import { useIntl } from 'react-intl';
import { useStyles } from './Header.styles';
import { General } from '../../Modules/LawPortal/Features/Settings/General';
import { getAuthProvider } from '../../Auth/AuthProvider';
import { UserContext } from '../../Context/UserContext/UserContext';
import { shutdown } from '@intercom/messenger-js-sdk';

export interface IHeaderSubMenuItem {
  title: React.ReactNode;
  to: string;
}

interface IHeaderProps {
  title: React.ReactNode;
  subMenu?: IHeaderSubMenuItem[];
  sidebarOpen?: boolean;
  handleSidebarToggle?: (open: boolean) => void;
}


export const Header: FC<IHeaderProps> = ({
  title,
  subMenu,
  sidebarOpen,
  handleSidebarToggle,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const location = useLocation();

  const isLgUp = useMediaQuery((theme) => (theme as any).breakpoints.up('lg'));

  const activeSubMenu = subMenu
    ?.filter(
      (menu) =>
        location.pathname === menu.to ||
        location.pathname.startsWith(menu.to + '/')
    )
    .sort((a, b) => b.to.length - a.to.length)
    .find(() => true);

  const getClassName = (menu: IHeaderSubMenuItem) =>
    menu === activeSubMenu ? classes.active : classes.inactive;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { firstName, lastName } = useContext(UserContext);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    const authProvider = getAuthProvider();
    localStorage.clear();
    shutdown();
    authProvider.logoutRedirect({
  postLogoutRedirectUri: window.location.origin,
});

    setAnchorEl(null);
  };


  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className={clsx(classes.root, { [classes.shift]: sidebarOpen })}
        classes={{
          positionFixed: clsx({ [classes.positionFixed]: !sidebarOpen }),
        }}
      >
        <Toolbar>
          {!isLgUp && (
            <Tooltip
              title={formatMessage({
                id: 'component.sidebar.show',
                defaultMessage: 'Show menu',
              })}
            >
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => handleSidebarToggle?.(true)}
                className={clsx(classes.menuButton, {
                  [classes.hide]: sidebarOpen,
                })}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}

          <div
            style={{
              width: '97%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h2" component="h1">
              {title}
            </Typography>

            <Avatar
              className={classes.avatarColor}
              onClick={handleAvatarClick}
            >
              {firstName?.charAt(0)}
              {lastName?.charAt(0)}
            </Avatar>
          </div>
        </Toolbar>

        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
          style={{ marginTop: 40 }}
        >
          <General setAnchorEl={() => setAnchorEl(null)} />
          <MenuItem onClick={handleLogout} style={{ gap: 10 }}>
            <MeetingRoomIcon />
            {formatMessage({
              id: 'component.sidebar.logout',
              defaultMessage: 'Logout',
            })}
          </MenuItem>
        </Menu>

        {subMenu && (
          <Toolbar
            className={classes.subMenu}
            component="nav"
            variant="dense"
            disableGutters
          >
            <div className={classes.subMenuWrapper}>
              {subMenu.map((menu, index) => (
                <Button
                  key={`submenu-${index}`}
                  component={Link}
                  to={menu.to}
                  variant="text"
                  className={clsx(
                    classes.subMenuButton,
                    getClassName(menu)
                  )}
                >
                  {menu.title}
                </Button>
              ))}
            </div>
          </Toolbar>
        )}
      </AppBar>

      <div className={classes.toolbar} />
      {subMenu && <div className={classes.subMenuToolbar} />}
    </>
  );
};
